import { useEffect, useRef, useState } from 'react';

import GenericModal from 'components/app-components/modals/GenericModal';
import { Button } from 'components/app-components/ui/button';
import { Input } from 'components/app-components/ui/input';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useTheraFernClient } from 'hooks/useFern';
import { formatErrorMessage } from 'utils/error';

interface AddFundsToCardModalProps {
  open: boolean;
  onClose: () => void;
  customerId?: string;
  cardAccountId?: string;
  /** USDC wallet balance as a decimal string (e.g. "12.50"). May be undefined while loading. */
  walletBalance?: string;
  /** Card available balance, formatted for display (e.g. "$10.00"). */
  cardBalanceDisplay?: string;
  /** Called after a successful transfer so callers can refetch wallet + card. */
  onTransferred: () => Promise<void> | void;
}

/**
 * Validates a non-empty amount string. Caller must skip the call when amount is empty —
 * empty is not an error, it's just "button disabled, do nothing".
 */
const validateAmount = (amount: string, max: number): string | null => {
  if (!/^\d+(\.\d{1,2})?$/.test(amount)) return 'Enter a valid amount (up to 2 decimals)';
  const parsed = Number(amount);
  if (parsed <= 0) return 'Amount must be greater than 0';
  if (parsed > max) return 'Amount exceeds wallet balance';
  return null;
};

export const AddFundsToCardModal = ({
  open,
  onClose,
  customerId,
  cardAccountId,
  walletBalance,
  cardBalanceDisplay,
  onTransferred,
}: AddFundsToCardModalProps) => {
  const { theraFernApi } = useTheraFernClient();
  const maxAmount = Number(walletBalance ?? 0);
  const [amount, setAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Synchronous in-flight guard so a fast double-click can't fire two requests
  // before React re-renders the disabled state.
  const inFlightRef = useRef(false);

  // Pre-fill with the full wallet balance whenever the modal opens.
  useEffect(() => {
    if (open) {
      setAmount(walletBalance ? Number(walletBalance).toFixed(2) : '');
    }
  }, [open, walletBalance]);

  const validationError = amount ? validateAmount(amount, maxAmount) : null;
  const canSubmit =
    Boolean(customerId) &&
    Boolean(cardAccountId) &&
    Boolean(amount) &&
    !validationError &&
    !isSubmitting &&
    maxAmount > 0;

  const handleSubmit = async () => {
    if (inFlightRef.current || !canSubmit) return;
    inFlightRef.current = true;
    setIsSubmitting(true);
    try {
      await theraFernApi.thera.bridge.bridgeApi.addFundsToCard(customerId, cardAccountId, { amount });
      theraToast.success('Funds transferred. Your card balance will be updated shortly.');
      await onTransferred();
      onClose();
    } catch (error) {
      const message = formatErrorMessage(error, 'Failed to transfer funds to card');
      theraToast.error({
        message,
        error,
        options: { id: 'add-funds-to-card-failed' },
      });
    } finally {
      inFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <GenericModal
      open={open}
      onClose={isSubmitting ? () => {} : onClose}
      preventOutsideClick={isSubmitting}
      title="Add Funds to Card"
      subTitle="Move USDC from your Thera wallet to your Thera Card."
      width="md:min-w-md md:max-w-md w-full"
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-md border border-neutral-200 p-3">
            <p className="text-xs font-medium text-neutral-500">Wallet balance (USDC)</p>
            <p className="mt-1 text-sm font-semibold text-neutral-900">
              {walletBalance ? Number(walletBalance).toFixed(2) : '0.00'}
            </p>
          </div>
          <div className="rounded-md border border-neutral-200 p-3">
            <p className="text-xs font-medium text-neutral-500">Card balance</p>
            <p className="mt-1 text-sm font-semibold text-neutral-900">{cardBalanceDisplay ?? '—'}</p>
          </div>
        </div>

        <div>
          <label htmlFor="add-funds-amount" className="text-sm font-medium text-neutral-700">
            Amount
          </label>
          <Input
            id="add-funds-amount"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            disabled={isSubmitting}
            className="mt-1"
          />
          {validationError && <p className="mt-1 text-xs text-therared">{validationError}</p>}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="primary" onClick={handleSubmit} loading={isSubmitting} disabled={!canSubmit}>
            Transfer
          </Button>
        </div>
      </div>
    </GenericModal>
  );
};
